/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Users,
  FileText,
  IndianRupee,
  Package,
  Database,
  Download
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { supabase } from '../utils/supabaseClient';
import { getTotalCustomers } from '../utils/db/customers';
import { exportToExcel } from '../utils/exportData';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#6366F1', '#10B981']; // Indigo for GST, Green for Non-GST

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalCustomers: 0,
    totalEarnings: 0,
    totalBookings: 0,
    monthlyInvoices: 0,
    monthlyEarnings: 0,
    gstRevenue: 0,
    gstRevenue: 0,
    nonGstRevenue: 0,
    monthlyTrendData: [],
    gstSplitData: []
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    // Load dashboard data from database
    const loadDashboardData = async () => {
      if (!user) return;

      try {
        setIsLoading(true);

        // Fetch total bookings count accurately (not limited)
        const { count: totalBookingsCount, error: totalBookingsError } = await supabase
          .from('bookings')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);
        if (totalBookingsError) throw totalBookingsError;

        // Fetch total customers
        const customerCount = await getTotalCustomers(user.id);

        // Fetch all invoices for accurate stats (count and earnings)
        const { data: allInvoices, error: statsError } = await supabase
          .from('invoices')
          .select('grand_total, invoice_date, invoice_no')
          .eq('user_id', user.id);

        if (statsError) throw statsError;

        // Calculate stats from all invoices
        const totalInvoices = allInvoices.length;
        const totalEarnings = allInvoices.reduce((sum, invoice) => sum + (invoice.grand_total || 0), 0);

        // Split revenue into GST and Non-GST
        const gstRevenue = allInvoices
          .filter(inv => !inv.invoice_no?.startsWith('NG-'))
          .reduce((sum, inv) => sum + (inv.grand_total || 0), 0);

        const nonGstRevenue = allInvoices
          .filter(inv => inv.invoice_no?.startsWith('NG-'))
          .reduce((sum, inv) => sum + (inv.grand_total || 0), 0);

        // Calculate monthly stats
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();

        const currentMonthInvoices = allInvoices.filter(invoice => {
          const invoiceDate = new Date(invoice.invoice_date);
          return invoiceDate.getMonth() === currentMonth &&
            invoiceDate.getFullYear() === currentYear;
        });

        const monthlyInvoices = currentMonthInvoices.length;
        const monthlyEarnings = currentMonthInvoices.reduce((sum, invoice) => sum + (invoice.grand_total || 0), 0);

        // Generate trend data for the last 6 months
        const trendData = Array.from({length: 6}, (_, i) => {
          const d = new Date();
          d.setMonth(d.getMonth() - i);
          return { name: d.toLocaleString('default', { month: 'short' }), year: d.getFullYear(), m: d.getMonth(), sum: 0 };
        }).reverse();
        
        allInvoices.forEach(inv => {
          const d = new Date(inv.invoice_date);
          const bucket = trendData.find(b => b.m === d.getMonth() && b.year === d.getFullYear());
          if (bucket) {
            bucket.sum += (inv.grand_total || 0);
          }
        });

        setStats({
          totalInvoices,
          totalCustomers: customerCount,
          totalEarnings,
          totalBookings: totalBookingsCount || 0,
          monthlyInvoices,
          monthlyEarnings,
          gstRevenue,
          nonGstRevenue,
          monthlyTrendData: trendData.map(b => ({ name: b.name, Revenue: b.sum })),
          gstSplitData: [
            { name: 'GST', value: gstRevenue },
            { name: 'Non-GST', value: nonGstRevenue }
          ]
        });
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, [user]);

  const handleExportData = async (dataType = 'all') => {
    if (!user) return;

    try {
      setIsExporting(true);
      await exportToExcel(user.id, dataType);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, change }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          {change && (
            <p className="text-sm text-green-600 flex items-center">
              <TrendingUp className="h-4 w-4 mr-1" />
              {change}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Overview of your franchise billing system</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total GST Revenue"
          value={`₹${stats.gstRevenue.toLocaleString()}`}
          icon={IndianRupee}
          color="bg-primary-500"
          change={null}
        />
        <StatCard
          title="Total Non-GST Revenue"
          value={`₹${stats.nonGstRevenue.toLocaleString()}`}
          icon={IndianRupee}
          color="bg-green-500"
        />
        <StatCard
          title="Total Earnings"
          value={stats.totalEarnings > 0 ? `₹${stats.totalEarnings.toLocaleString()}` : '₹0'}
          icon={IndianRupee}
          color="bg-yellow-500"
          change={stats.monthlyEarnings > 0 ? `₹${stats.monthlyEarnings.toLocaleString()} this month` : null}
        />
        <StatCard
          title="Total Bookings"
          value={stats.totalBookings}
          icon={Package}
          color="bg-purple-500"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Trend Area Chart */}
        <div className="bg-white rounded-lg shadow lg:col-span-2">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Revenue Trend (Last 6 Months)</h3>
          </div>
          <div className="p-6 h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.monthlyTrendData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366F1" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} tickFormatter={(value) => `₹${value >= 1000 ? (value / 1000) + 'k' : value}`} />
                <RechartsTooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                <Area type="monotone" dataKey="Revenue" stroke="#6366F1" fillOpacity={1} fill="url(#colorRevenue)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* GST vs Non-GST Split */}
        <div className="bg-white rounded-lg shadow lg:col-span-1">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Revenue Split</h3>
          </div>
          <div className="p-6 h-80 flex items-center justify-center">
            {stats.totalEarnings > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.gstSplitData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {stats.gstSplitData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
            ) : (
                <div className="text-gray-400 text-center">
                   <p>No revenue data available</p>
                </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/booking')}
              className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <Package className="h-6 w-6 mr-2 text-gray-400" />
              <span className="text-gray-600">Add New Booking</span>
            </button>
            <button
              onClick={() => navigate('/invoice-generator')}
              className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <FileText className="h-6 w-6 mr-2 text-gray-400" />
              <span className="text-gray-600">Generate Invoice</span>
            </button>
            <button
              onClick={() => navigate('/customers')}
              className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <Users className="h-6 w-6 mr-2 text-gray-400" />
              <span className="text-gray-600">Add Customer</span>
            </button>
          </div>
        </div>
      </div>

      {/* Data Management - Moved to dedicated page
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Database className="h-5 w-5 mr-2" />
            Data Management
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-primary-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-primary-900 mb-2">Export Data</h4>
              <p className="text-sm text-primary-700 mb-3">Download your data in Excel format for backup or reporting</p>
              <button
                onClick={() => handleExportData('all')}
                disabled={isExporting}
                className="flex items-center w-full px-4 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <Download className="h-5 w-5 mr-2" />
                {isExporting ? 'Exporting...' : 'Export All Data'}
              </button>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  onClick={() => handleExportData('invoices')}
                  disabled={isExporting}
                  className="flex items-center justify-center px-2 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-md hover:bg-primary-200"
                >
                  Invoices Only
                </button>
                <button
                  onClick={() => handleExportData('bookings')}
                  disabled={isExporting}
                  className="flex items-center justify-center px-2 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-md hover:bg-primary-200"
                >
                  Bookings Only
                </button>
              </div>
            </div>
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Data Summary</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Total Invoices:</span>
                    <span className="font-medium">{stats.totalInvoices}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Total Bookings:</span>
                    <span className="font-medium">{stats.totalBookings}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Total Earnings:</span>
                    <span className="font-medium">₹{stats.totalEarnings.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      */}
    </div>
  );
};

export default Dashboard;